import { type FC, useEffect, useState } from 'react';

import { httpGetFaceRecognitionPersonsByPersonIdFacesByFaceIdImageUrl } from '@iconik-io/iconik-api/src/services/ml';

import { useAppDispatch } from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import SelectPersonFieldWrapper from '../../../ui/forms/field_wrappers/select_persons/select_persons';
import LoadingBar from '../../../ui/loading_bar/loading_bar';
import { NEW_OPTION_VALUE_REGEX } from '../../../ui/select_field/select_field';
import SendHint from '../../../ui/send_hint/send_hint';
import { usePersonSelect } from './hooks/use_person_select';
import type { PersonSelectProps } from './types';

import styles from './recognized_persons.module.css';

const PersonSelect: FC<PersonSelectProps> = ({
  onSetShowPersonSelect,
  segments,
  person,
  actionType,
  rowType,
  onDeactivate,
}: PersonSelectProps) => {
  const dispatch = useAppDispatch();
  const [saveAsNewPerson, setSaveAsNewPerson] = useState(false);
  const [instanceImageUrl, setInstanceImageUrl] = useState<string | undefined>(
    undefined,
  );

  const { personBody, setPersonBody, handleSave, status } = usePersonSelect({
    onSetShowPersonSelect,
    segments,
    person,
    actionType: actionType ?? (saveAsNewPerson ? 'add' : 'change'),
    onDeactivate,
  });

  const isPending = status === 'pending';

  useEffect(() => {
    if (person.id && person.person_version_face_id && !instanceImageUrl) {
      dispatch(
        httpGetFaceRecognitionPersonsByPersonIdFacesByFaceIdImageUrl(
          person.id,
          person.person_version_face_id,
        ),
      ).then((response) => {
        setInstanceImageUrl(response.body.url ?? undefined);
      });
    }
  }, [person, instanceImageUrl]);

  const personImageUrl = person.image_url ?? undefined;

  return (
    <>
      {isPending && <LoadingBar className={styles.loading} />}
      <form
        className={styles.change_person_container}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave({ saveAsNewPerson, analyticsPlace: 'panel' });
        }}
      >
        <SelectPersonFieldWrapper
          isCreatorMode
          currentPersonId={person.id}
          newPersonImageUrl={
            rowType === 'new' ? personImageUrl : instanceImageUrl
          }
          placeholder="Select person or type a new name"
          onChange={(newPersonId) => {
            if (!newPersonId || NEW_OPTION_VALUE_REGEX.test(newPersonId)) {
              setSaveAsNewPerson(true);
              return;
            }
            setSaveAsNewPerson(false);
            setPersonBody({
              ...personBody,
              new_person_name: undefined,
              new_person_id: newPersonId,
            });
          }}
          onNewOptionNameSelected={(name) => {
            if (!name) {
              return;
            }
            setPersonBody({
              ...personBody,
              new_person_id: undefined,
              new_person_name: name,
            });
          }}
          disabled={isPending}
          canClear
        />
        <div className={styles.person_form_actions}>
          <Button
            type="button"
            variant="warning"
            className={styles.cancel_action}
            disabled={isPending}
            onClick={() => {
              if (onSetShowPersonSelect) {
                onSetShowPersonSelect(false);
              }
            }}
          >
            Cancel
          </Button>
          <SendHint />
          <Button type="submit" disabled={isPending} variant="success">
            Save
          </Button>
        </div>
      </form>
    </>
  );
};

export default PersonSelect;
